import { defineQuery } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql } from "../schema";
import { channelMemberGuard } from "./access-filters";

// Phase 6: agent-queryability shared queries for kind='email' messages.
// Imperative one-shot reads. Outer messages scope is gated by
// channelMemberGuard so a caller never sees an email card from a
// channel they can't already access.
//
// NOTE (no current consumers): scaffolding for future agent paths.
// See calendar-events.ts header for the indexing concern that applies
// when these become hot in zero-cache.

export const emailMessagesQueries = {
  // Email cards where `email_from_email` matches `email`.
  // Backed by the partial index `idx_messages_email_from_email`
  // (migration 092) which is SARGable for kind='email' rows.
  fromSender: defineQuery(
    z.object({
      email: z.string(),
      since: z.number().optional(),
      limit: z.number().default(100),
    }),
    ({ args: { email, since, limit }, ctx: { sub } }) => {
      const base = zql.messages
        .where("kind", "email")
        .where("email_from_email", email)
        .whereExists("channel", channelMemberGuard(sub));
      const filtered = since ? base.where("created_at", ">=", since) : base;
      return filtered
        .orderBy("created_at", "desc")
        .orderBy("id", "desc")
        .limit(limit)
        .related("email_participants");
    },
  ),
  // Email cards where `email` appears as any participant role
  // (from / to / cc / bcc) — joins via the `email_participants`
  // side table. PK on the side table is (message_id, email, role)
  // so the join scans the email-prefix subset for each parent.
  withParticipant: defineQuery(
    z.object({
      email: z.string(),
      since: z.number().optional(),
      limit: z.number().default(100),
    }),
    ({ args: { email, since, limit }, ctx: { sub } }) => {
      const base = zql.messages
        .where("kind", "email")
        .whereExists("email_participants", (epq) => epq.where("email", email))
        .whereExists("channel", channelMemberGuard(sub));
      const filtered = since ? base.where("created_at", ">=", since) : base;
      return filtered
        .orderBy("created_at", "desc")
        .orderBy("id", "desc")
        .limit(limit)
        .related("email_participants");
    },
  ),
};
